import {lazy, FC, Suspense} from 'react'
import {Route, Routes, Navigate} from 'react-router-dom'
import {MasterLayout} from '../../_metronic/layout/MasterLayout'
import TopBarProgress from 'react-topbar-progress-indicator'
import {DashboardWrapper} from '../pages/dashboard/DashboardWrapper'
// import {MenuTestPage} from '../pages/MenuTestPage'
import {getCSSVariableValue} from '../../_metronic/assets/ts/_utils'
import {WithChildren} from '../../_metronic/helpers'
import {useAuth} from '../modules/auth'
import {getDefaultLandingRoute} from './routeHelper'

// import BuilderPageWrapper from '../pages/layout-builder/BuilderPageWrapper'

const lazyRetry = (componentImport: () => Promise<any>) =>
  lazy(async () => {
    const pageHasAlreadyBeenForceRefreshed = JSON.parse(
      window.sessionStorage.getItem('page-has-been-force-refreshed') || 'false'
    )

    try {
      const component = await componentImport()
      window.sessionStorage.setItem('page-has-been-force-refreshed', 'false')
      return component
    } catch (error) {
      if (!pageHasAlreadyBeenForceRefreshed) {
        window.sessionStorage.setItem('page-has-been-force-refreshed', 'true')
        window.location.reload()
      }
      throw error
    }
  })

const BillingPage = lazyRetry(() => import('../modules/apps/billing/BillingPage'))
const ClientsPage = lazyRetry(() => import('../modules/apps/clients/ClientsPage'))
const CdrPage = lazyRetry(() => import('../modules/apps/cdr/CdrPage'))
const CdrImportPage = lazyRetry(() => import('../modules/apps/cdr/CdrImportPage'))
const InvoicePage = lazyRetry(() => import('../modules/apps/invoice/InvoicePage'))
const SettingsPage = lazyRetry(() => import('../modules/apps/settings/SettingsPage'))
const RoleManagementPage = lazyRetry(() => import('../modules/apps/role-management/RoleManagementPage'))
const UserManagementPage = lazyRetry(() => import('../modules/apps/user-management/UserManagementPage'))
const ProfilePage = lazyRetry(() => import('../modules/apps/profile/ProfilePage'))
const ActivityLogPage = lazyRetry(() => import('../modules/apps/activity-log/ActivityLogPage'))
const ProductsPage = lazyRetry(() => import('../modules/apps/products/ProductsPage'))
const DataSourcesPage = lazyRetry(() => import('../modules/apps/data-sources/DataSourcesPage'))

const PrivateRoutes = () => {
  const {currentUser} = useAuth()
  
  // Parse allowedMenus string (e.g. "home,monitoring_active_calls") to an array
  const allowedMenusStr = currentUser?.role?.allowedMenus || ''
  const allowedMenus = allowedMenusStr.split(',').map(m => m.trim())

  const hasAccess = (menuKey: string) => allowedMenus.includes(menuKey)
    const roleName = currentUser?.role?.name?.toUpperCase()
  const defaultLandingRoute = getDefaultLandingRoute(currentUser)
  const canAccessDashboard =
    hasAccess('dashboard') ||
    roleName === 'ADMIN' ||
    roleName === 'SUPERADMIN'
  const canAccessActivityLog =
    hasAccess('activity_log') ||
    roleName === 'ADMIN' ||
    roleName === 'SUPERADMIN' ||
    roleName === 'CLIENT'

  return (
    <Routes>
      <Route element={<MasterLayout />}>
        {/* Redirect to dynamic landing page after successful login */}
        <Route path='auth/*' element={<Navigate to={defaultLandingRoute} replace />} />
        {/* Dashboard Page - Protected by RBAC */}
        {canAccessDashboard ? (
          <Route path='dashboard' element={<DashboardWrapper />} />
        ) : (
          <Route path='dashboard' element={<Navigate to={defaultLandingRoute} replace />} />
        )}
        
        {/* Lazy Modules - conditionally rendered based on RBAC */}
        {hasAccess('billing_rates') && (
          <Route
            path='billing/*'
            element={
              <SuspensedView>
                <BillingPage />
              </SuspensedView>
            }
          />
        )}
        
        {hasAccess('clients') && (
          <Route
            path='clients/*'
            element={
              <SuspensedView>
                <ClientsPage />
              </SuspensedView>
            }
          />
        )}
        
        {(hasAccess('products') || hasAccess('billing_rates') || roleName === 'ADMIN' || roleName === 'SUPERADMIN') && (
          <Route
            path='products/*'
            element={
              <SuspensedView>
                <ProductsPage />
              </SuspensedView>
            }
          />
        )}
        
        {hasAccess('cdr_report') && (
          <Route
            path='cdr/import'
            element={
              <SuspensedView>
                <CdrImportPage />
              </SuspensedView>
            }
          />
        )}

        {(hasAccess('data_source') || hasAccess('cdr_report') || roleName === 'ADMIN' || roleName === 'SUPERADMIN') && (
          <Route
            path='data-sources/*'
            element={
              <SuspensedView>
                <DataSourcesPage />
              </SuspensedView>
            }
          />
        )}
        
        <Route
          path='cdr/*'
          element={
            <SuspensedView>
              <CdrPage />
            </SuspensedView>
          }
        />
        
        {hasAccess('invoice') && (
          <Route
            path='invoice/*'
            element={
              <SuspensedView>
                <InvoicePage />
              </SuspensedView>
            }
          />
        )}
        
        {hasAccess('global_settings') && (
          <Route
            path='settings/*'
            element={
              <SuspensedView>
                <SettingsPage />
              </SuspensedView>
            }
          />
        )}

        {hasAccess('role_management') && (
          <Route
            path='roles/*'
            element={
              <SuspensedView>
                <RoleManagementPage />
              </SuspensedView>
            }
          />
        )}

        {hasAccess('user_management') && (
          <Route
            path='users/*'
            element={
              <SuspensedView>
                <UserManagementPage />
              </SuspensedView>
            }
          />
        )}

        {/* Profile Page (Accessible to all logged-in users) */}
        <Route
          path='profile/*'
          element={
            <SuspensedView>
              <ProfilePage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/profile/*'
          element={
            <SuspensedView>
              <ProfilePage />
            </SuspensedView>
          }
        />

        {/* Activity Log Page - Accessible to ADMIN and CLIENT */}
        {canAccessActivityLog && (
          <Route
            path='activity-log/*'
            element={
              <SuspensedView>
                <ActivityLogPage />
              </SuspensedView>
            }
          />
        )}

        {/* Page Not Found */}
        <Route path='*' element={<Navigate to='/error/404' />} />
      </Route>
    </Routes>
  )
}
const SuspensedView: FC<WithChildren> = ({children}) => {
  const baseColor = getCSSVariableValue('--bs-primary')
  TopBarProgress.config({
    barColors: {
      '0': baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  })
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>
}
export {PrivateRoutes}